import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { CheckCircle, Download, Eye, FileText, MessageCircle, Paperclip, StickyNote, CreditCard, Shield } from "lucide-react";
import { PACKAGE_OPTIONS } from "./constants";

export const ConfirmationForm = ({ register, setValue, formValues, customers, handleViewInvoice, handleDownloadInvoice, handleAttachInvoice }) => (
  <>
    <Card className="border-2 border-green-300">
      <div className="bg-green-500 px-3 py-2">
        <h3 className="font-bold text-white flex items-center gap-2 text-sm">
          <CheckCircle className="h-4 w-4" />
          Booking Details
        </h3>
      </div>
      <CardContent className="p-3 space-y-3">
        <div>
          <Label className="text-xs font-semibold">Moving Date</Label>
          <Input 
            {...register("moving_date")} 
            placeholder="e.g. Friday, 15 March 2026"
            className="h-12 mt-1 border-2 border-blue-400 focus:border-blue-600 font-semibold text-base"
            style={{ fontSize: '16px' }}
            data-testid="confirm-moving-date"
          />
        </div>
        <div>
          <Label className="text-xs font-semibold">Arrival Time</Label>
          <Input 
            {...register("arrival_time")} 
            placeholder="8:00 AM - 9:00 AM"
            className="h-12 mt-1 border-2 border-gray-300 focus:border-blue-500 font-semibold"
            style={{ fontSize: '16px' }}
          />
        </div>
        <div>
          <Label className="text-xs">Selected Package</Label>
          <Select value={formValues.selected_package} onValueChange={(val) => setValue("selected_package", val)}>
            <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PACKAGE_OPTIONS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Total (£)</Label>
            <Input type="number" {...register("service_total", { valueAsNumber: true })} className="h-9 mt-1" />
          </div>
          <div>
            <Label className="text-xs">Deposit (£)</Label>
            <Input type="number" {...register("deposit_amount", { valueAsNumber: true })} className="h-9 mt-1" />
          </div>
        </div>
      </CardContent>
    </Card>

    {/* CUSTOM MESSAGE TO CUSTOMER */}
    <Card className="border-2 border-purple-400 bg-purple-50" data-testid="custom-recommendation-confirm">
      <div className="bg-purple-600 px-3 py-2">
        <h3 className="font-bold text-white flex items-center gap-2 text-sm">
          <MessageCircle className="h-4 w-4" />
          Your Message to Customer
        </h3>
      </div>
      <CardContent className="p-3 space-y-3">
        <p className="text-xs text-purple-700">Write a personal recommendation or message to your customer:</p>
        
        <Textarea 
          {...register("custom_recommendation_message")} 
          placeholder="Example: You booked with us 2 crew members for your job. If you want a faster and smoother moving experience, we recommend booking one more professional mover. This will help reduce the time and make your move stress-free!

Or: Based on the size of your property, we suggest adding packing materials to protect your fragile items during transit."
          className="min-h-[120px] text-sm border-2 border-purple-300 focus:border-purple-500"
          style={{ fontSize: '15px' }}
        />
        
        {/* Quick Templates */}
        <div>
          <Label className="text-xs font-semibold text-purple-700 mb-2 block">Quick Templates (tap to use):</Label>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setValue("custom_recommendation_message", 
                `You booked with us ${formValues.booked_crew_count || '2'} crew members and a ${formValues.booked_van_type || '3.5T Van'} for your move. For a faster and smoother experience, we recommend upgrading to ${formValues.recommended_crew_count || '3'} professional movers. This will significantly reduce loading/unloading time and ensure your belongings are handled with extra care!`
              )}
              className="px-3 py-1.5 bg-purple-100 hover:bg-purple-200 text-purple-700 text-xs rounded-full border border-purple-300"
            >
              🚚 Crew Upgrade
            </button>
            <button
              type="button"
              onClick={() => setValue("custom_recommendation_message", 
                `We noticed your move includes fragile items. We highly recommend our professional packing service to ensure everything arrives safely. Our team uses high-quality packing materials and techniques to protect your valuables during transit.`
              )}
              className="px-3 py-1.5 bg-purple-100 hover:bg-purple-200 text-purple-700 text-xs rounded-full border border-purple-300"
            >
              📦 Packing Service
            </button>
            <button
              type="button"
              onClick={() => setValue("custom_recommendation_message", 
                `Based on the distance and property size, we recommend booking our premium insurance cover (up to £50,000) for complete peace of mind. This covers any accidental damage during the move.`
              )}
              className="px-3 py-1.5 bg-purple-100 hover:bg-purple-200 text-purple-700 text-xs rounded-full border border-purple-300"
            >
              🛡️ Insurance
            </button>
            <button
              type="button"
              onClick={() => setValue("custom_recommendation_message", 
                `Thank you for choosing HomeGo Removals! As a reminder, please ensure all items are boxed and ready on the moving day. Our team will handle the heavy lifting, furniture dismantling, and reassembly at your new home. See you soon!`
              )}
              className="px-3 py-1.5 bg-purple-100 hover:bg-purple-200 text-purple-700 text-xs rounded-full border border-purple-300"
            >
              👋 Friendly Reminder
            </button>
            <button
              type="button"
              onClick={() => setValue("custom_recommendation_message", 
                `We understand moving can be stressful, especially if you'd like to take it easy or have mobility concerns. For your peace of mind, we offer a full packing service where our professional team will carefully pack ALL your belongings - from fragile china to heavy books. You just relax and let us handle everything! This is perfect for elderly customers, busy professionals, or anyone who wants a completely stress-free move. Ask us about adding packing to your booking!`
              )}
              className="px-3 py-1.5 bg-purple-100 hover:bg-purple-200 text-purple-700 text-xs rounded-full border border-purple-300"
            >
              👴 Packing for Elderly/Relaxed Move
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input 
            type="checkbox" 
            {...register("show_custom_message")}
            className="h-4 w-4 rounded border-gray-300"
          />
          <Label className="text-xs">Show this message in email</Label>
        </div>
      </CardContent>
    </Card>

    <Card className="border-2 border-[#D4AF37]/40">
      <div className="bg-yellow-50 px-3 py-2 border-b border-[#D4AF37]">
        <h3 className="font-bold text-amber-700 flex items-center gap-2 text-sm">
          <StickyNote className="h-4 w-4" />
          Special Notes
        </h3>
      </div>
      <CardContent className="p-3">
        <Textarea {...register("special_notes")} placeholder="Notes..." className="min-h-[80px]" />
      </CardContent>
    </Card>

    <Card className="border-2 border-green-300">
      <div className="bg-green-50 px-3 py-2 border-b border-green-300">
        <h3 className="font-bold text-green-700 flex items-center gap-2 text-sm">
          <CreditCard className="h-4 w-4" />
          Bank Details
        </h3>
      </div>
      <CardContent className="p-3 space-y-2">
        <div>
          <Label className="text-xs">Account Name</Label>
          <Input {...register("bank_account_name")} className="h-9 mt-1 font-semibold" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Sort Code</Label>
            <Input {...register("bank_sort_code")} className="h-9 mt-1 font-mono" />
          </div>
          <div>
            <Label className="text-xs">Account No</Label>
            <Input {...register("bank_account_number")} className="h-9 mt-1 font-mono" />
          </div>
        </div>
      </CardContent>
    </Card>

    <Card className="border-2 border-blue-200">
      <div className="bg-blue-50 px-3 py-2 border-b border-blue-200">
        <h3 className="font-bold text-blue-700 text-sm">Pay By Card (Optional)</h3>
      </div>
      <CardContent className="p-3">
        <Input {...register("payment_link")} placeholder="https://..." className="h-9" />
        <p className="text-xs text-gray-500 mt-1">Leave empty to hide card payment section</p>
      </CardContent>
    </Card>

    {/* Invoice Editor - Confirmation: Deposit Invoice for Customer to Pay */}
    <Card className="border-2 border-emerald-400 bg-emerald-50" data-testid="invoice-editor-confirmation">
      <div className="bg-emerald-600 px-3 py-2">
        <h3 className="font-bold text-white flex items-center gap-2 text-sm">
          <FileText className="h-4 w-4" />
          Deposit Invoice (For Customer to Pay)
        </h3>
      </div>
      <CardContent className="p-3 space-y-3">
        <p className="text-xs text-emerald-700">Generate invoice for customer to pay their deposit</p>
        
        {/* Package Selector */}
        <div>
          <Label className="text-xs font-semibold">Select Package</Label>
          <select 
            className="w-full h-9 mt-1 px-3 border rounded-md text-sm"
            value={formValues.invoice_service_name}
            onChange={(e) => {
              const pkg = e.target.value;
              setValue("invoice_service_name", pkg);
              // Auto-fill description based on package
              const descriptions = {
                "Man & Van": "1 Driver + Van, Loading assistance",
                "Economy Move": "Loading & unloading, placing boxes in chosen rooms",
                "Comfort Move": "Dismantling & reassembling furniture, Loading & unloading, Placing boxes in correct rooms",
                "Comfort Plus": "Full packing of all items, Fragile packing, Dismantling & reassembling, Loading & unloading",
                "Premium Move": "Complete packing, Full unpacking, Furniture placement, Debris removal"
              };
              setValue("invoice_service_description", descriptions[pkg] || "");
              // Auto-fill price
              const prices = {
                "Man & Van": formValues.man_and_van_price,
                "Economy Move": formValues.economy_move_price,
                "Comfort Move": formValues.comfort_move_price,
                "Comfort Plus": formValues.comfort_plus_price,
                "Premium Move": formValues.premium_price
              };
              setValue("invoice_move_price", prices[pkg] || formValues.comfort_move_price);
            }}
          >
            <option value="Man & Van">Man & Van</option>
            <option value="Economy Move">Economy Move</option>
            <option value="Comfort Move">Comfort Move</option>
            <option value="Comfort Plus">Comfort Plus</option>
            <option value="Premium Move">Premium Move</option>
          </select>
        </div>

        {/* Move Price */}
        <div>
          <Label className="text-xs font-semibold">Total Move Price (£)</Label>
          <Input 
            {...register("invoice_move_price")} 
            type="number"
            placeholder="485" 
            className="h-9 mt-1" 
          />
        </div>

        {/* Deposit Amount to Pay */}
        <div>
          <Label className="text-xs font-semibold">Deposit to Pay (£)</Label>
          <Input 
            {...register("invoice_deposit_amount")} 
            type="number"
            placeholder="100" 
            className="h-9 mt-1 border-emerald-400 bg-emerald-50" 
          />
        </div>

        {/* Remaining Balance - calculated */}
        <div className="bg-amber-100 border border-amber-300 rounded-md p-3">
          <div className="flex justify-between items-center">
            <Label className="text-xs font-semibold text-amber-800">Remaining Balance:</Label>
            <span className="text-lg font-bold text-amber-700">
              £{((parseFloat(formValues.invoice_move_price) || 0) - (parseFloat(formValues.invoice_deposit_amount) || 0)).toFixed(2)}
            </span>
          </div>
          <p className="text-xs text-amber-700 mt-2 font-medium">
            ⚠️ Pay remaining balance on job day before leaving the old property
          </p>
        </div>

        <div>
          <Label className="text-xs">Service Description</Label>
          <Textarea {...register("invoice_service_description")} placeholder="Describe what's included..." className="min-h-[60px] mt-1 text-xs" />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Customer Phone</Label>
            <Input {...register("invoice_customer_phone")} placeholder="07xxx" className="h-9 mt-1" />
          </div>
          <div>
            <Label className="text-xs">Customer Email</Label>
            <Input {...register("invoice_customer_email")} placeholder="email" className="h-9 mt-1" />
          </div>
        </div>
        
        {/* Invoice Action Buttons */}
        <div className="grid grid-cols-2 gap-2 pt-2">
          <Button 
            onClick={() => handleViewInvoice('deposit')} 
            variant="outline"
            className="h-10 border-emerald-500 text-emerald-700 hover:bg-emerald-50"
          >
            <Eye className="h-4 w-4 mr-2" />
            View Deposit Invoice
          </Button>
          <Button 
            onClick={() => handleDownloadInvoice('deposit')} 
            className="h-10 bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <Download className="h-4 w-4 mr-2" />
            Download PDF
          </Button>
        </div>
        <Button 
          onClick={() => handleAttachInvoice('deposit')} 
          className="w-full h-10 mt-2 bg-black hover:bg-zinc-800 text-[#D4AF37] font-bold border border-[#D4AF37]"
          data-testid="attach-deposit-invoice-button"
        >
          <Paperclip className="h-4 w-4 mr-2" />
          Attach Invoice to This Email
        </Button>
      </CardContent>
    </Card>

    {/* WORKING HOURS & OVERTIME POLICY */}
    <Card className="border-2 border-gray-700 bg-gray-50" data-testid="overtime-policy-card">
      <div className="bg-gray-900 px-3 py-2 flex items-center justify-between">
        <h3 className="font-bold text-amber-400 flex items-center gap-2 text-sm">
          <Shield className="h-4 w-4" />
          Working Hours & Overtime Policy
        </h3>
        <label className="flex items-center gap-2 cursor-pointer">
          <span className="text-xs text-white font-medium">Show in Email</span>
          <input
            type="checkbox"
            {...register("show_overtime_policy")}
            data-testid="show-overtime-policy-checkbox"
            className="h-4 w-4 rounded border-white"
          />
        </label>
      </div>
      {formValues.show_overtime_policy && (
        <CardContent className="p-3 space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs font-semibold text-gray-700">Start Time</Label>
              <Input {...register("work_hours_start")} placeholder="9:00 AM" className="h-10 mt-1 font-bold text-center" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-gray-700">End Time</Label>
              <Input {...register("work_hours_end")} placeholder="5:00 PM" className="h-10 mt-1 font-bold text-center" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-red-700">Overtime £/hour</Label>
              <Input {...register("overtime_rate")} placeholder="120" className="h-10 mt-1 border-red-300 font-bold text-center" />
            </div>
          </div>
          <p className="text-[11px] text-gray-500">Restates the working-hours and overtime terms agreed at quote stage.</p>
        </CardContent>
      )}
    </Card>
  </>
);
